import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { selectProduct, setVizLoading } from '../../../../store/visualizerSlice';
import Product from '../../Product/Product';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './IslandCabinets.module.css';

// Generate level label from style value (L1 -> Level 1, L6 -> Level 6)
const getLevelLabel = (style) => {
    if (!style) return style;
    const match = style.match(/^L(\d+)$/);
    if (match) {
        return `Level ${match[1]}`;
    }
    return style;
};

export default function IslandCabinets({ 
    stepIndex,
    room = '',
    onViewAllApplied, 
    onNext, 
    onPrevious 
}) {
    const dispatch = useDispatch();
    const stepSelections = useSelector(state => state.visualizer.stepSelections);
    const [activeTab, setActiveTab] = useState('style'); // 'style' or 'colors'
    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDoorId, setSelectedDoorId] = useState(null);
    const [selectedFinishId, setSelectedFinishId] = useState(null);
    const [selectedDrawerId, setSelectedDrawerId] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [colorData, setColorData] = useState([]);
    const [colorLoading, setColorLoading] = useState(false);

    // Selection keys: StepName_Room_Door and StepName_Room_Color
    const doorSelectionKey = `IslandCabinets_${room}_Door`;
    const colorSelectionKey = `IslandCabinets_${room}_Color`;

    // Restore selections from Redux on mount
    useEffect(() => {
        const savedDoor = stepSelections[doorSelectionKey];
        const savedColor = stepSelections[colorSelectionKey];
        if (savedDoor?.product) {
            setSelectedProductId(savedDoor.product.id);
            setSelectedDoorId(savedDoor.product.id);
            setSelectedProfile(savedDoor.product); // Restore profile for color filtering
            // Parse descriptor to restore finish and drawer IDs
            if (savedDoor.product.descriptor) {
                const parts = savedDoor.product.descriptor.split(',').map(s => s.trim());
                if (parts.length >= 2) {
                    setSelectedFinishId(parts[0]);
                    setSelectedDrawerId(parts[1]);
                }
            }
        }
        if (savedColor?.product) {
            setSelectedColor(savedColor.product);
        }
    }, [stepSelections, doorSelectionKey, colorSelectionKey]);

    // Fetch door products
    useEffect(() => {
        setLoading(true);
        setError(null);

        const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/9713.json`;
        
        fetch(URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch products: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                // Filter products by categoryName == "Cabinet Doors"
                const filtered = (Array.isArray(data) ? data : []).filter(
                    p => p.categoryName === 'Cabinet Doors'
                );
                setProductData(filtered);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching products:', error);
                setError(error.message);
                setLoading(false);
            });
    }, []);

    // Get unique levels from data and sort them
    const sortedLevels = useMemo(() => {
        const levels = [...new Set(productData.map(p => p.style).filter(Boolean))];
        // Sort by numeric value (L1, L2, L3, L4, L5, ... L10+)
        return levels.sort((a, b) => {
            const numA = parseInt(a.replace('L', '')) || 0;
            const numB = parseInt(b.replace('L', '')) || 0;
            return numA - numB;
        });
    }, [productData]);

    // Group products by level (style attribute)
    // Products without style go to "Included" group at the top
    const groupedProducts = useMemo(() => {
        const groups = {};
        
        // First, add products without tier (Included group)
        const includedProducts = productData.filter(p => !p.style);
        if (includedProducts.length > 0) {
            groups['Included'] = includedProducts;
        }
        
        // Then add tiered products
        sortedLevels.forEach(level => {
            const levelProducts = productData.filter(p => p.style === level);
            if (levelProducts.length > 0) {
                groups[level] = levelProducts;
            }
        });
        
        return groups;
    }, [productData, sortedLevels]);

    // Fetch and filter colors based on selected profile's defaultRoomSceneSpecifier
    useEffect(() => {
        if (!selectedProfile?.defaultRoomSceneSpecifier) {
            setColorData([]);
            return;
        }

        // Parse allowed color IDs from defaultRoomSceneSpecifier
        const allowedColorIds = selectedProfile.defaultRoomSceneSpecifier
            .split(',')
            .map(id => id.trim())
            .filter(Boolean);

        if (allowedColorIds.length === 0) {
            setColorData([]);
            return;
        }

        setColorLoading(true);

        fetch(`https://lizard.chameleonpower.com/CDN/ProductsBySeries/9714.json`)
            .then(response => response.json())
            .then(data => {
                // Filter colors to only those in the allowed list
                const filtered = (Array.isArray(data) ? data : []).filter(
                    p => allowedColorIds.includes(String(p.id))
                );
                setColorData(filtered);
                setColorLoading(false);
            })
            .catch(err => {
                console.error('Error fetching colors:', err);
                setColorData([]);
                setColorLoading(false);
            });
    }, [selectedProfile]);

    // Get unique color levels and sort them
    const sortedColorLevels = useMemo(() => {
        const levels = [...new Set(colorData.map(p => p.style).filter(Boolean))];
        return levels.sort((a, b) => {
            const numA = parseInt(a.replace('L', '')) || 0;
            const numB = parseInt(b.replace('L', '')) || 0;
            return numA - numB;
        });
    }, [colorData]);

    // Group colors by level (style attribute)
    const groupedColors = useMemo(() => {
        return sortedColorLevels.reduce((acc, level) => {
            const levelProducts = colorData.filter(p => p.style === level);
            if (levelProducts.length > 0) {
                acc[level] = levelProducts;
            }
            return acc;
        }, {});
    }, [colorData, sortedColorLevels]);

    // Apply when both door and color are selected (and color is valid for profile)
    useEffect(() => {
        if (selectedDoorId && selectedColor && selectedProfile) {
            // Validate that selected color is allowed for current profile
            if (selectedProfile.defaultRoomSceneSpecifier) {
                const allowedColorIds = selectedProfile.defaultRoomSceneSpecifier
                    .split(',')
                    .map(id => id.trim())
                    .filter(Boolean);
                
                const isColorValid = allowedColorIds.includes(String(selectedColor.id));
                if (!isColorValid) {
                    console.log('IslandCabinets - Skipping apply: color not valid for profile');
                    return;
                }
            }
            applyIslandCabinetSelections();
        }
    }, [selectedDoorId, selectedFinishId, selectedDrawerId, selectedColor, selectedProfile]);

    const handleDoorSelect = (product) => {
        setSelectedProductId(product.id);
        setSelectedDoorId(product.id);
        setSelectedProfile(product); // Store full profile for color filtering
        
        // Check if current color is valid for the new profile
        if (selectedColor && product.defaultRoomSceneSpecifier) {
            const allowedColorIds = product.defaultRoomSceneSpecifier
                .split(',')
                .map(id => id.trim())
                .filter(Boolean);
            
            const isColorValid = allowedColorIds.includes(String(selectedColor.id));
            
            if (!isColorValid) {
                // Reset color and switch to colors tab
                setSelectedColor(null);
                setActiveTab('colors');
                console.log('IslandCabinets - Color reset: not valid for new profile');
            }
        } else if (selectedColor) {
            // No defaultRoomSceneSpecifier means reset color
            setSelectedColor(null);
            setActiveTab('colors');
        }
        
        // Parse descriptor to get finish and drawer IDs
        // Format: "finishId , drawerId"
        if (product.descriptor) {
            const parts = product.descriptor.split(',').map(s => s.trim());
            if (parts.length >= 2) {
                setSelectedFinishId(parts[0]);
                setSelectedDrawerId(parts[1]);
            }
        }
        
        console.log('IslandCabinets - Door selected:', product.color || product.name);
        console.log('IslandCabinets - Door ID:', product.id);
        console.log('IslandCabinets - Descriptor:', product.descriptor);
        console.log('IslandCabinets - Allowed colors:', product.defaultRoomSceneSpecifier);
        
        // Store selection in Redux
        dispatch(selectProduct({
            product,
            surfaceName: 'IslandCabinets_Door',
            stepIndex,
            selectionKey: doorSelectionKey
        }));
    };

    const handleColorSelect = (product) => {
        setSelectedColor(product);
        
        console.log('IslandCabinets - Color selected:', product.color || product.name);
        console.log('IslandCabinets - Color ID:', product.id);
        
        // Store selection in Redux
        dispatch(selectProduct({
            product,
            surfaceName: 'IslandCabinets_Color',
            stepIndex,
            selectionKey: colorSelectionKey
        }));
    };

    const applyIslandCabinetSelections = () => {
        const chamViz = document.getElementById('chamViz');
        if (!chamViz) {
            console.error('IslandCabinets - chamViz element not found');
            return;
        }

        if (!selectedDoorId || !selectedColor?.id) {
            console.error('IslandCabinets - Missing door or color selection');
            return;
        }

        const colorId = selectedColor.id;
        
        // Build sel strings
        const doorSel = `${selectedDoorId}(${colorId})`;
        const finishSel = selectedFinishId ? `${selectedFinishId}(${colorId})` : doorSel;
        
        console.log('IslandCabinets - Applying selections:');
        console.log('  Door sel:', doorSel, '-> Island Cabinet Doors');
        console.log('  Finish sel:', finishSel, '-> Island Cabinet Base');
        
        dispatch(setVizLoading(true));
        
        // Apply to Island Cabinet Base and Island Cabinet Doors
        chamViz.applyByFilter(
            doorSel,
            "",
            function(newSel) {
                // Island Cabinet Doors - use door sel
                if (this.name === 'Island Cabinet Doors') {
                    return doorSel;
                }
                // Island Cabinet Base - use finish sel
                if (this.name === 'Island Cabinet Base') {
                    return finishSel;
                }
                return this.sel;
            },
            true
        );
    };

    const handleViewAllApplied = () => {
        if (onViewAllApplied) {
            onViewAllApplied();
        }
    };

    return (
        <div className={styles.container}>
            <StepNavigation
                title="Island Cabinets"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={handleViewAllApplied}
            />

            {/* Tab Navigation */}
            <div className={styles.tabContainer}>
                <button
                    className={`${styles.tab} ${activeTab === 'style' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('style')}
                >
                    STYLE
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'colors' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('colors')}
                >
                    COLORS
                </button>
            </div>

            {/* Selection Status */}
            <div className={styles.selectionStatus}>
                <span className={selectedDoorId ? styles.selected : ''}>
                    Door: {selectedDoorId ? 'Selected' : 'Not selected'}
                </span>
                <span className={selectedColor ? styles.selected : ''}>
                    Color: {selectedColor ? (selectedColor.color || selectedColor.name) : 'Not selected'}
                </span>
            </div>

            {/* Product List - Style Tab (with tier grouping) */}
            {activeTab === 'style' && (
                <div className={`${styles.productListContainer} ${styles.cabinetsProducts}`} data-series="9713">
                    {loading && (
                        <div className={styles.message}>Loading products...</div>
                    )}

                    {error && (
                        <div className={styles.errorMessage}>Error: {error}</div>
                    )}

                    {!loading && !error && Object.keys(groupedProducts).length === 0 && (
                        <div className={styles.message}>No products available.</div>
                    )}

                    {!loading && !error && Object.entries(groupedProducts).map(([level, products]) => (
                        <div key={level} className={styles.tierSection}>
                            <div className={styles.tierHeader}>
                                <span className={styles.tierTitle}>
                                    {level === 'Included' ? 'Included' : getLevelLabel(level)}
                                </span>
                                <div className={styles.tierLine}></div>
                            </div>
                            <ul className={styles.productGrid}>
                                {products.map(product => (
                                    <Product
                                        key={product.id}
                                        id={product.id}
                                        specifier={product.specifier}
                                        focus={500}
                                        size="256"
                                        title={product.color || ''}
                                        description={product.name || ''}
                                        series="9713"
                                        onSelect={(id) => {
                                            const selected = products.find(p => p.id === id);
                                            if (selected) handleDoorSelect(selected);
                                        }}
                                        isActive={product.id === selectedProductId}
                                    />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {/* Product List - Colors Tab */}
            {activeTab === 'colors' && (
                <div className={`${styles.productListContainer} ${styles.cabinetsProducts}`}>
                    {!selectedProfile && (
                        <div className={styles.message}>Please select a cabinet style first.</div>
                    )}

                    {selectedProfile && colorLoading && (
                        <div className={styles.message}>Loading colors...</div>
                    )}

                    {selectedProfile && !colorLoading && colorData.length === 0 && (
                        <div className={styles.message}>No colors available for this style.</div>
                    )}

                    {selectedProfile && !colorLoading && colorData.length > 0 && (
                        <ul className={styles.productGrid}>
                            {colorData.map(product => (
                                <Product
                                    key={product.id}
                                    id={product.id}
                                    specifier={product.specifier}
                                    focus={500}
                                    size="256"
                                    title={product.color || ''}
                                    description={product.name || ''}
                                    series="9714"
                                    onSelect={(id) => {
                                        const selected = colorData.find(p => p.id === id);
                                        if (selected) handleColorSelect(selected);
                                    }}
                                    isActive={product.id === selectedColor?.id}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

IslandCabinets.propTypes = {
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
